import { OverlayContainer } from '@angular/cdk/overlay';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, TitleStrategy } from '@angular/router';
import { OwlDateTimeModule } from '@danielmoncada/angular-datetime-picker';
import { OwlMomentDateTimeModule } from '@danielmoncada/angular-datetime-picker-moment-adapter';
import { MTX_POPOVER_DEFAULT_OPTIONS } from '@ng-matero/extensions/popover';
import { FormlyMaterialModule } from '@ngx-formly/material';
import { CDN_HOST, formlyConfig, OrdersServiceWithApplyToAll } from '@vectronic/formly-components';
import {
  keycloakBearerInterceptor,
  KeycloakMockService,
  KeycloakService,
} from '@vectronic/keycloak-angular';
import { OrdersService } from '@vectronic/oof/shared/data-access';
import { POPOVER_DEFAULT_CONFIG } from '@vectronic/shared/configurations';
import {
  BACKEND_HOST,
  backendHostInterceptor,
  CustomOverlayContainer,
  DEVICE_TYPE_RESOLVER,
  RMA_DEVICE_NETWORK_STATUS,
  VectronicErrorHandler,
} from '@vectronic/util';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { authInterceptor } from './shared/interceptors/auth-interceptor';
import { errorInterceptor } from './shared/interceptors/error-interceptor';
import { CustomerDeviceTypeResolver } from './shared/service/customer-device-type-resolver.service';
import { CustomerRmaDeviceNetworkStatusService } from './shared/service/customer-rma-device-network-status.service';
import { MyVectronicTitleStrategy } from './shared/service/title.strategy';

const initializeKeycloak = (keycloak: KeycloakService) => () =>
  keycloak.init({
    config: {
      url: environment.keycloakUrl,
      realm: environment.keycloakRealm,
      clientId: environment.keycloakClientId,
    },
    loadUserProfileAtStartUp: true,
    initOptions: {
      onLoad: 'check-sso',
      silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
      checkLoginIframe: true,
      logoutMethod: 'GET',
    },
  });

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    provideHttpClient(
      withInterceptors([
        authInterceptor,
        errorInterceptor,
        backendHostInterceptor,
        keycloakBearerInterceptor,
      ]),
    ),
    provideAnimations(),
    {
      provide: TitleStrategy,
      useClass: MyVectronicTitleStrategy,
    },
    provideAppInitializer(() => {
      const initializerFn = initializeKeycloak(inject(KeycloakService));
      return initializerFn();
    }),
    {
      provide: KeycloakService,
      useFactory: () =>
        environment.useAuthInterceptor
          ? new KeycloakMockService(environment.username, environment.userrole)
          : new KeycloakService(),
    },
    {
      provide: ErrorHandler,
      useClass: VectronicErrorHandler,
    },
    { provide: OrdersServiceWithApplyToAll, useExisting: OrdersService },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        subscriptSizing: 'dynamic',
      },
    },
    {
      provide: BACKEND_HOST,
      useValue: environment.backendHost,
    },
    {
      provide: CDN_HOST,
      useValue: environment.cdnHost,
    },
    {
      provide: DEVICE_TYPE_RESOLVER,
      useClass: CustomerDeviceTypeResolver,
    },
    {
      provide: RMA_DEVICE_NETWORK_STATUS,
      useClass: CustomerRmaDeviceNetworkStatusService,
    },

    importProvidersFrom(MatDialogModule, MatSnackBarModule, FormlyMaterialModule),
    formlyConfig,
    {
      provide: MTX_POPOVER_DEFAULT_OPTIONS,
      useValue: POPOVER_DEFAULT_CONFIG,
    },
    { provide: OverlayContainer, useClass: CustomOverlayContainer },
    importProvidersFrom(OwlDateTimeModule, OwlMomentDateTimeModule),
  ],
};
